/**
 * TanStack Query hooks для Library API (Block L1 + L2.1).
 *
 * - useLibraries(kind, projectId?) — list own libraries (project_id filter optional, см. ниже)
 * - useLibrary(id)                  — full library with snapshot
 * - useCreateLibrary()              — supports optional project_id
 * - useUpdateLibrary()
 * - useDeleteLibrary()
 * - useApplyLibrary()
 *
 * Block L2.1 (TZ 2026-07-29):
 * - `useLibraries(kind, projectId?)`: якщо projectId передано — бекенд повертає
 *   project-scoped rows + personal bucket (NULL project_id). Якщо null/undefined —
 *   повний список (L1 compat).
 * - `useCreateLibrary({ project_id? })`: при створенні з поточним projectId FE
 *   прокидає поле project_id, library стає scoped до проекту.
 * - queryKey включає projectId, щоб кеш не змішував списки для різних проектів.
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryOptions,
} from '@tanstack/react-query';

import api from '@/lib/api';

export type LibraryKind = 'search' | 'generation' | 'matching' | 'files_transform';

export interface LibraryListItem {
  id: string;
  project_id: string | null;
  /** null = unified workspace library (P1.4); иначе — legacy per-kind. */
  kind: LibraryKind | null;
  name: string;
  description: string | null;
  last_applied_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface Library {
  id: string;
  account_id: string;
  project_id: string | null;
  /** null = unified workspace library (P1.4); иначе — legacy per-kind. */
  kind: LibraryKind | null;
  name: string;
  description: string | null;
  snapshot: Record<string, unknown>;
  last_applied_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface LibraryApplyResponse {
  library_id: string;
  /** null = unified workspace library (P1.4). */
  kind: LibraryKind | null;
  snapshot: Record<string, unknown>;
  warnings: string[];
}

// === Phase 2.3: files_transform apply response ===
// Расширенная форма для kind=files_transform. На бэке эндпоинт
// POST /libraries/{id}/apply требует { dataset_id } и возвращает
// резолвленный (effective) snapshot + warnings, а не сырой snapshot.
// FE использует поля ниже чтобы применить значения через существующие
// useSelectSheet / useRenameColumns / useSelectColumns.
export interface FilesTransformApplyResponse {
  dataset_id: string;
  library_id: string;
  selected_sheet: string | null;
  column_renames: Record<string, string>;
  selected_columns: string[];
  warnings: string[];
}

// === list ===

export function useLibraries(
  kind: LibraryKind | null,
  projectId?: string | null,
  options?: Partial<UseQueryOptions<{ items: LibraryListItem[]; total: number }>>,
) {
  // 'all' / 'personal' segments get distinct cache slots from per-project slots.
  const projectKey = projectId ?? 'personal';
  return useQuery<{ items: LibraryListItem[]; total: number }>({
    queryKey: ['libraries', kind ?? 'all', projectKey],
    queryFn: async () => {
      const params: Record<string, string> = {};
      if (kind) params.kind = kind;
      // Block L2.1: filter by project. 'personal' = null bucket + current project libs.
      if (projectId) params.project_id = projectId;
      return (
        await api.get<{ items: LibraryListItem[]; total: number }>('/libraries', {
          params,
        })
      ).data;
    },
    ...options,
  });
}

// === single ===

export function useLibrary(
  libraryId: string | null,
  options?: Partial<UseQueryOptions<Library>>,
) {
  return useQuery<Library>({
    queryKey: ['libraries', libraryId],
    queryFn: async () =>
      (await api.get<Library>(`/libraries/${libraryId}`)).data,
    enabled: Boolean(libraryId),
    ...options,
  });
}

// === mutations ===

export function useCreateLibrary() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: {
      // P1.4: null/omitted = unified workspace library.
      kind: LibraryKind | null;
      name: string;
      description?: string | null;
      // Block L2.1: optional project scope.
      project_id?: string | null;
      snapshot: Record<string, unknown>;
    }) => (await api.post<Library>('/libraries', body)).data,
    onSuccess: (data) => {
      // Invalidate every cached list (any projectId/any kind) — safer than
      // guessing which slice the new row belongs to.
      qc.invalidateQueries({ queryKey: ['libraries'] });
      qc.setQueryData(['libraries', data.id], data);
    },
  });
}

export function useUpdateLibrary() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      libraryId,
      body,
    }: {
      libraryId: string;
      body: { name?: string; description?: string | null; snapshot?: Record<string, unknown> };
    }) => (await api.patch<Library>(`/libraries/${libraryId}`, body)).data,
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['libraries'] });
      qc.setQueryData(['libraries', data.id], data);
    },
  });
}

export function useDeleteLibrary() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (libraryId: string) => {
      await api.delete(`/libraries/${libraryId}`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['libraries'] });
    },
  });
}

export function useApplyLibrary() {
  return useMutation({
    mutationFn: async (vars: {
      libraryId: string;
      // Phase 2.3: optional body — files_transform requires { dataset_id }.
      // Search / generation / matching — body omitted (backward compat).
      body?: { dataset_id?: string };
    }) =>
      (
        await api.post<LibraryApplyResponse | FilesTransformApplyResponse>(
          `/libraries/${vars.libraryId}/apply`,
          vars.body ?? undefined,
        )
      ).data,
  });
}